import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useChefsLab } from '../context/ChefsLabContext';

// Company config
const COMPANIES = ['psa', 'cgc', 'bgs', 'sgc', 'tag', 'ace'];
const COMPANY_LABELS = { psa: 'PSA', bgs: 'BGS', cgc: 'CGC', sgc: 'SGC', tag: 'TAG', ace: 'ACE' };
const GRADES = ['10', '9', '8', '7', '6', '5', '4', '3', '2', '1'];

// Back icon
const BackIcon = ({ color }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

// Trophy icon
const TrophyIcon = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={color} stroke="none">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
  </svg>
);

// Format price
const formatPrice = (price) => {
  if (price === null || price === undefined) return '--';
  return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ChefsLabCompare = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { selectedCard, researchData, preferences, calculateAverage, rawSources, rawSourcesLoading } = useChefsLab();

  const [selectedGrade, setSelectedGrade] = useState('10');

  // Best price from multi-source (for raw cards)
  const bestPrice = rawSources?.bestPrice || null;
  const multiSourceReady = !!rawSources && !rawSourcesLoading;

  // Calculate rankings for selected grade
  const rankings = useMemo(() => {
    if (!researchData?.evidence) return [];

    const companyData = COMPANIES.map(company => {
      // Get listings for this company and grade
      const listings = (researchData.evidence || []).filter(listing => {
        const listingCompany = listing.company?.toLowerCase();
        const listingGrade = listing.grade_number?.toString();
        return listingCompany === company && listingGrade === selectedGrade;
      });

      // Calculate average respecting preferences
      const average = calculateAverage(listings);
      const count = listings.length;

      return {
        company,
        label: COMPANY_LABELS[company],
        average,
        count,
      };
    });

    // Filter out companies with no data and sort by average price (highest first)
    return companyData
      .filter(c => c.count > 0 && c.average !== null)
      .sort((a, b) => b.average - a.average);
  }, [researchData, selectedGrade, calculateAverage]);

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.bg,
      paddingBottom: 'calc(100px + env(safe-area-inset-bottom))',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '16px 20px',
      borderBottom: `1px solid ${theme.border}`,
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      cursor: 'pointer',
    },
    headerInfo: {
      flex: 1,
    },
    title: {
      color: theme.text,
      fontSize: '18px',
      fontWeight: 600,
      margin: 0,
    },
    subtitle: {
      color: theme.textMuted,
      fontSize: '13px',
      margin: '2px 0 0 0',
    },
    cardThumb: {
      width: '48px',
      height: '67px',
      borderRadius: '6px',
      objectFit: 'cover',
      border: `1px solid ${theme.border}`,
    },
    content: {
      padding: '20px',
    },
    sectionTitle: {
      color: theme.textMuted,
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      margin: '0 0 12px 0',
    },
    gradeSelector: {
      display: 'flex',
      gap: '8px',
      overflowX: 'auto',
      paddingBottom: '8px',
      marginBottom: '24px',
    },
    gradeButton: {
      minWidth: '44px',
      height: '44px',
      borderRadius: '12px',
      border: `1px solid ${theme.border}`,
      backgroundColor: theme.bgCard,
      color: theme.text,
      fontSize: '16px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    gradeButtonActive: {
      backgroundColor: theme.accent,
      border: `1px solid ${theme.accent}`,
      color: '#fff',
    },
    rankingsContainer: {
      backgroundColor: theme.bgCard,
      borderRadius: '16px',
      border: `1px solid ${theme.border}`,
      overflow: 'hidden',
    },
    rankingHeader: {
      display: 'grid',
      gridTemplateColumns: '50px 1fr 100px 60px',
      padding: '12px 16px',
      borderBottom: `1px solid ${theme.border}`,
      backgroundColor: theme.bgInput,
    },
    rankingHeaderCell: {
      color: theme.textMuted,
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
    },
    rankingRow: {
      display: 'grid',
      gridTemplateColumns: '50px 1fr 100px 60px',
      padding: '16px',
      alignItems: 'center',
      borderBottom: `1px solid ${theme.border}`,
    },
    rankingRowLast: {
      borderBottom: 'none',
    },
    rankNumber: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    rankText: {
      color: theme.text,
      fontSize: '18px',
      fontWeight: 700,
    },
    companyName: {
      color: theme.text,
      fontSize: '16px',
      fontWeight: 600,
    },
    avgPrice: {
      color: '#22c55e',
      fontSize: '16px',
      fontWeight: 700,
      textAlign: 'right',
    },
    salesCount: {
      color: theme.textMuted,
      fontSize: '14px',
      textAlign: 'right',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: theme.textMuted,
    },
    bestPriceSection: {
      marginTop: '24px',
    },
    bestPriceLoading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      padding: '24px 0',
    },
    spinner: {
      width: 24,
      height: 24,
      borderRadius: '50%',
      border: `3px solid ${theme.border}`,
      borderTop: '3px solid #22c55e',
      animation: 'spin 0.8s linear infinite',
    },
    bestPriceCard: {
      backgroundColor: 'rgba(34, 197, 94, 0.08)',
      border: '1px solid #22c55e',
      borderRadius: '12px',
      padding: '16px',
    },
    bestPriceLabel: {
      fontSize: '12px',
      fontWeight: 700,
      color: '#22c55e',
      textTransform: 'uppercase',
    },
    bestPriceValue: {
      fontSize: '22px',
      fontWeight: 700,
      color: '#22c55e',
    },
    bestPriceReasoning: {
      fontSize: '12px',
      color: theme.textMuted,
      marginTop: '6px',
      display: 'block',
    },
    bestPriceEmpty: {
      textAlign: 'center',
      padding: '20px',
    },
    summarySection: {
      marginTop: '20px',
    },
    summaryCard: {
      backgroundColor: theme.bgCard,
      border: `1px solid ${theme.border}`,
      borderRadius: '12px',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    summaryText: {
      fontSize: '13px',
      lineHeight: '1.6',
      color: theme.textSecondary,
      margin: 0,
    },
    preferencesNote: {
      marginTop: '16px',
      padding: '12px',
      backgroundColor: theme.bgInput,
      borderRadius: '10px',
    },
    preferencesText: {
      color: theme.textSecondary,
      fontSize: '12px',
      margin: 0,
      textAlign: 'center',
    },
  };

  // Get medal color for rank
  const getMedalColor = (rank) => {
    if (rank === 1) return '#fbbf24'; // Gold
    if (rank === 2) return '#9ca3af'; // Silver
    if (rank === 3) return '#cd7f32'; // Bronze
    return null;
  };

  if (!selectedCard) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate('/chefs-lab')}>
            <BackIcon color={theme.text} />
          </button>
          <div style={styles.headerInfo}>
            <h1 style={styles.title}>Compare Companies</h1>
          </div>
        </div>
        <div style={styles.emptyState}>
          <p>No card selected</p>
          <p style={{ fontSize: '13px', marginTop: '8px' }}>
            Go back and select a card first
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <BackIcon color={theme.text} />
        </button>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>Compare Companies</h1>
          <p style={styles.subtitle}>
            {selectedCard.name} - {selectedCard.set_name}
          </p>
        </div>
        {selectedCard.digital_image_url && (
          <img
            src={selectedCard.digital_image_url}
            alt={selectedCard.name}
            style={styles.cardThumb}
          />
        )}
      </div>

      <div style={styles.content}>
        {/* Grade Selector */}
        <p style={styles.sectionTitle}>Select Grade</p>
        <div style={styles.gradeSelector}>
          {GRADES.map(grade => (
            <motion.button
              key={grade}
              style={{
                ...styles.gradeButton,
                ...(selectedGrade === grade ? styles.gradeButtonActive : {}),
              }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedGrade(grade)}
            >
              {grade}
            </motion.button>
          ))}
        </div>

        {/* Rankings Table */}
        <p style={styles.sectionTitle}>Grade {selectedGrade} Rankings</p>

        {rankings.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No data for Grade {selectedGrade}</p>
            <p style={{ fontSize: '13px', marginTop: '8px' }}>
              Try selecting a different grade
            </p>
          </div>
        ) : (
          <div style={styles.rankingsContainer}>
            <div style={styles.rankingHeader}>
              <span style={styles.rankingHeaderCell}>Rank</span>
              <span style={styles.rankingHeaderCell}>Company</span>
              <span style={{ ...styles.rankingHeaderCell, textAlign: 'right' }}>Avg Price</span>
              <span style={{ ...styles.rankingHeaderCell, textAlign: 'right' }}>Sales</span>
            </div>

            {rankings.map((item, index) => {
              const rank = index + 1;
              const medalColor = getMedalColor(rank);
              const isLast = index === rankings.length - 1;

              return (
                <motion.div
                  key={item.company}
                  style={{
                    ...styles.rankingRow,
                    ...(isLast ? styles.rankingRowLast : {}),
                  }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div style={styles.rankNumber}>
                    {medalColor && <TrophyIcon color={medalColor} />}
                    <span style={styles.rankText}>{rank}</span>
                  </div>
                  <span style={styles.companyName}>{item.label}</span>
                  <span style={styles.avgPrice}>{formatPrice(item.average)}</span>
                  <span style={styles.salesCount}>{item.count}</span>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Best Price (Multi-Source) */}
        <div style={styles.bestPriceSection}>
          <p style={styles.sectionTitle}>Best Price (All Sources)</p>
          {rawSourcesLoading && !bestPrice && (
            <div style={styles.bestPriceLoading}>
              <div style={styles.spinner} />
              <span style={{ color: theme.textMuted, fontSize: 13 }}>
                Loading Poketrace + Market + Gemini...
              </span>
            </div>
          )}
          {bestPrice && (
            <div style={styles.bestPriceCard}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={styles.bestPriceLabel}>NM Best Price</span>
                <span style={styles.bestPriceValue}>
                  {bestPrice.isRange && bestPrice.priceLow
                    ? `${formatPrice(bestPrice.priceLow)} - ${formatPrice(bestPrice.priceHigh)}`
                    : formatPrice(bestPrice.price)
                  }
                </span>
              </div>
              <span style={styles.bestPriceReasoning}>
                {bestPrice.reasoning} ({bestPrice.source})
              </span>
            </div>
          )}
          {multiSourceReady && !bestPrice && (
            <div style={styles.bestPriceEmpty}>
              <span style={{ color: theme.textMuted, fontSize: 13 }}>
                No best price available from sources
              </span>
            </div>
          )}
        </div>

        {/* Gemini Summary */}
        {rawSources?.gemini?.summary && (
          <div style={styles.summarySection}>
            <p style={styles.sectionTitle}>Market Intelligence</p>
            <div style={styles.summaryCard}>
              {rawSources.gemini.usedGrounding && (
                <p style={{ fontSize: 11, color: '#eab308', fontWeight: 600, margin: '0 0 8px 0' }}>
                  Web search was used -- automated data was flagged as unreliable
                  {rawSources.gemini.groundingReason ? `: ${rawSources.gemini.groundingReason}` : ''}
                </p>
              )}
              <p style={styles.summaryText}>{rawSources.gemini.summary}</p>
              {rawSources.gemini.groundingMetadata?.sources?.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  {rawSources.gemini.groundingMetadata.sources.map((src, idx) => (
                    <a
                      key={idx}
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 10, color: '#0ea5e9', display: 'block', marginTop: 2, textDecoration: 'none' }}
                    >
                      {src.title || src.url}
                    </a>
                  ))}
                </div>
              )}
              {rawSources.gemini.confidence && (
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  padding: '3px 8px',
                  borderRadius: 4,
                  backgroundColor: rawSources.gemini.confidence === 'high' ? 'rgba(34, 197, 94, 0.15)' :
                    rawSources.gemini.confidence === 'medium' ? 'rgba(234, 179, 8, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                  color: rawSources.gemini.confidence === 'high' ? '#22c55e' :
                    rawSources.gemini.confidence === 'medium' ? '#eab308' : '#ef4444',
                  alignSelf: 'flex-start',
                }}>
                  {rawSources.gemini.confidence} confidence
                </span>
              )}
            </div>
          </div>
        )}

        {/* Preferences Note */}
        <div style={styles.preferencesNote}>
          <p style={styles.preferencesText}>
            Averages calculated using your current cost preferences
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChefsLabCompare;
